import { normalizeDocument, exportDocument, UnsupportedFormatError } from './document.js';
import { ConflictError } from './library.js';

function check({ data, error }) {
  if (error?.code === '40001') throw new ConflictError();
  if (error) throw new Error(error.message || 'Cloud storage is unavailable. Your draft is kept in this browser.', { cause: error });
  return data;
}

/** Same storage interface as the local library; a successful write is a server commit. */
export function createCloudLibrary({ client, userId, drafts, preferences = globalThis.localStorage }) {
  let closed = false;
  function table(name) {
    if (closed) throw new Error('Your account changed. Sign in again to continue.');
    return client.from(`linework_${name}`);
  }
  async function rpc(name, args) {
    if (closed) throw new Error('Your account changed. Sign in again to continue.');
    // The server also checks this expected identity. A token changing while a
    // request waits for refresh cannot write this editor's data into a new account.
    return check(await client.rpc(`linework_${name}`, { ...args, p_owner: userId }));
  }
  const own = (query) => query.eq('owner_id', userId);
  function row(r) {
    return {
      id: r.id, title: r.title ?? r.document?.title, payload: r.document, revision: r.revision,
      folderId: r.folder_id, view: r.view, createdAt: r.created_at,
      updatedAt: r.updated_at, deletedAt: r.deleted_at,
      nodeCount: r.node_count ?? r.document?.canvas?.nodes?.length ?? 0,
    };
  }
  // PostgREST has a default row limit. Paginate libraries instead of silently hiding diagrams.
  async function all(build) {
    const result = [];
    for (let start = 0; ; start += 500) {
      const page = check(await build().range(start, start + 499));
      result.push(...page);
      if (page.length < 500) return result;
    }
  }
  async function list({ trash = false } = {}) {
    return (await all(() => {
      const query = own(table('canvases').select('id,title,node_count,revision,folder_id,updated_at,deleted_at'));
      return (trash ? query.gt('deleted_at', 0) : query.eq('deleted_at', 0)).order('updated_at', { ascending: false }).order('id');
    })).map(row);
  }
  async function listFolders() {
    return (await all(() => own(table('folders').select('*')).order('created_at').order('id')))
      .map(r => ({ id: r.id, name: r.name, createdAt: r.created_at }));
  }
  async function open(id) {
    const r = check(await own(table('canvases').select('*')).eq('id', id).single());
    try { return { ...row(r), doc: normalizeDocument(r.document), recovered: false }; }
    catch (error) {
      if (error instanceof UnsupportedFormatError) throw error;
      for (const cp of r.checkpoints || []) {
        try { return { ...row(r), doc: normalizeDocument(cp.payload), recovered: true }; } catch {}
      }
      throw new Error('This diagram could not be read. Import a JSON backup to recover it.', { cause: error });
    }
  }
  async function write(doc, { expectedRevision, view = null, checkpointLabel = null, requestId, folderId = null }) {
    return rpc('write', {
      p_id: doc.id, p_expected: expectedRevision, p_document: exportDocument(doc),
      p_view: view, p_label: checkpointLabel, p_request: requestId, p_folder: folderId,
    });
  }
  async function create(document, { view = null, reason = 'Created', folderId = null, id = crypto.randomUUID(), requestId = crypto.randomUUID() } = {}) {
    const doc = normalizeDocument(exportDocument(document));
    doc.id = id;
    const result = await write(doc, { expectedRevision: 0, view, checkpointLabel: reason, requestId, folderId });
    return { id, doc, view, folderId, deletedAt: 0, ...result };
  }
  // The same queue item keeps its request id across a lost response and retry.
  async function save(doc, { expectedRevision, view, checkpointLabel, draftToken, requestId = draftToken || crypto.randomUUID() }) {
    await drafts?.flush();
    const result = await write(doc, { expectedRevision, view, checkpointLabel, requestId });
    // A journal cleanup failure must not turn a committed write into a failed save.
    await drafts?.ack(doc.id, draftToken).catch(() => {});
    return result;
  }
  async function change(id, expectedRevision, action, folderId = null) {
    return rpc('change', { p_id: id, p_expected: expectedRevision, p_action: action, p_folder: folderId, p_request: crypto.randomUUID() });
  }
  async function history(id) {
    return check(await own(table('canvases').select('checkpoints')).eq('id', id).single()).checkpoints;
  }
  function folderName(value) {
    const name = String(value || '').trim();
    if (!name || name.length > 80) throw new Error('Use a folder name between 1 and 80 characters.');
    return name;
  }
  return {
    cloud: true, list, listFolders, open, create, save, history,
    stageDraft: (doc, view, label, revision) => drafts?.stage(doc, view, label, revision),
    discardDraft: id => drafts?.discard(id),
    async createFolder(value) {
      const r = check(await table('folders').insert({ owner_id: userId, id: crypto.randomUUID(), name: folderName(value) }).select().single());
      return { id: r.id, name: r.name, createdAt: r.created_at };
    },
    async renameFolder(id, value) {
      check(await own(table('folders').update({ name: folderName(value) })).eq('id', id).select('id').single());
    },
    moveCanvas: (id, folderId, revision) => change(id, revision, 'move', folderId),
    trash: (id, revision) => change(id, revision, 'trash'),
    async restore(id) {
      const r = await open(id);
      await change(id, r.revision, 'restore');
      return open(id);
    },
    async checkpointDocument(checkpointId, canvasId) {
      const cp = (await history(canvasId)).find(c => c.id === checkpointId);
      if (!cp) throw new Error('Checkpoint not found.');
      return normalizeDocument(cp.payload);
    },
    getPreference: async key => {
      try { return JSON.parse(preferences?.getItem(`linework:${userId}:${key}`) || 'null'); } catch { return null; }
    },
    setPreference: async (key, value) => {
      try { preferences?.setItem(`linework:${userId}:${key}`, JSON.stringify(value)); } catch {}
    },
    // Viewport is device-local and does not create document revision conflicts.
    saveView: async () => {},
    importLocal: snapshot => rpc('import', { p_source: snapshot.sourceId, p_snapshot: snapshot }),
    async recoverDrafts() {
      const entries = await drafts.list();
      for (const entry of entries) {
        const copy = normalizeDocument(exportDocument(entry.doc));
        copy.title += ' — recovered draft';
        await create(copy, { view: entry.view, reason: 'Recovered unsaved edits', id: `recovered:${entry.token}`, requestId: entry.token });
        await drafts.remove(entry);
      }
      return entries.length;
    },
    close() { closed = true; },
  };
}
